// Speculative responses stay outside the query cache until an authenticated
// route consumes them. Token changes and failed verification destroy the slot.
type Slot = { token: string; url: string; controller: AbortController; result: Promise<Response>; started: number };
const slots = new Map<string, Slot>();
const active = new Set<AbortController>();
let cacheBypassed = false;

export async function invalidateStartupCache(token: string) {
  discardStartupRead();
  if (import.meta.env.VITE_STARTUP_READ_CACHE !== 'true') return;
  try {
    const url = new URL('startup/invalidate', ((import.meta.env.VITE_BLOXX_DATA_URL as string) || 'https://data.bloxx.page').replace(/\/?$/, '/'));
    const response = await fetch(url, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, signal: AbortSignal.timeout(5000) });
    if (!response.ok) cacheBypassed = true;
  } catch { cacheBypassed = true; }
}

export function startupReadUrl(endpoint: string, cached = true) {
  if (cached && !cacheBypassed && import.meta.env.VITE_STARTUP_READ_CACHE === 'true') {
    const path = endpoint.replace('admin/overview/summary', 'startup/overview').replace('admin/keywords/query', 'startup/keywords');
    return new URL(path, ((import.meta.env.VITE_BLOXX_DATA_URL as string) || 'https://data.bloxx.page').replace(/\/?$/, '/')).href;
  }
  return new URL(endpoint, new URL(import.meta.env.VITE_XANO_CMS_URL.replace(/\/?$/, '/'), window.location.origin)).href;
}

export function discardStartupRead() {
  for (const controller of active) controller.abort();
  active.clear();
  slots.clear();
}

export function beginStartupRead(token: string, url: string) {
  const existing = slots.get(url);
  if (existing?.token === token && !existing.controller.signal.aborted && performance.now() - existing.started < 15000) return;
  existing?.controller.abort();
  if (slots.size >= 3) discardStartupRead();
  const controller = new AbortController();
  active.add(controller);
  const started = performance.now();
  const result = fetch(url, {
    headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
    signal: AbortSignal.any([controller.signal, AbortSignal.timeout(15000)]),
  });
  // Verification can fail before a route consumes this promise.
  void result.catch(() => undefined);
  slots.set(url, { token, url, controller, result, started });
}

export function consumeStartupRead(token: string, url: string, signal?: AbortSignal): Promise<Response> | undefined {
  const read = slots.get(url);
  if (!read) return;
  slots.delete(url);
  if (read.token !== token || read.url !== url || performance.now() - read.started > 15000) {
    read.controller.abort();
    return;
  }
  const abort = () => read.controller.abort();
  if (signal?.aborted) abort();
  signal?.addEventListener("abort", abort, { once: true });
  return read.result.finally(() => signal?.removeEventListener("abort", abort));
}

export function primeStartupRoute(token: string) {
  const flag = import.meta.env.VITE_STARTUP_PARALLEL;
  const enabled = flag === 'true' || (flag === 'canary' && sessionStorage.getItem('bloxx_startup_parallel_canary') === 'true');
  if (!enabled || window.location.hostname.endsWith(".pages.dev")) return;
  const path = window.location.pathname;
  const table = /^\/tables\/([a-zA-Z0-9_-]+)\/([a-zA-Z0-9_-]+)$/.exec(path);
  if (table) {
    const base = ((import.meta.env.VITE_BLOXX_DATA_URL as string) || 'https://data.bloxx.page').replace(/\/$/, '');
    const target = `${base}/workspaces/${table[1]}/tables/${table[2]}`;
    beginStartupRead(token, target);
    beginStartupRead(token, `${target}/rows?limit=500&offset=0`);
    return;
  }
  // /keywords now redirects to keyword research, so the old keyword list is not preloaded.
  // Overview reads the summary; it lives at /overview now that "/" is the sites list.
  const endpoint = path === "/overview" && import.meta.env.VITE_STARTUP_SUMMARY === "true" ? "admin/overview/summary" : null;
  if (!endpoint) return;
  beginStartupRead(token, startupReadUrl(endpoint));
}
